// SPDX-License-Identifier: GPL-3.0-or-later
// Text of the run for the HUD, the draft, the level select and the game over.
// The functions are pure. They return strings only. The caller writes them with `textContent`.
import type { ShotId } from "../src/table/callouts.ts";
import { formatPoints } from "../src/table/callouts.ts";
import { FRENZY_HITS } from "../src/table/run/timed-events.ts";
import type {
  BossState,
  ExtraBallState,
  PerkDef,
  RunSignals,
  StageDef,
  StageGoal,
  TimedEventKind,
  TimedEventState,
} from "../src/table/run/types.ts";

/** The name of each ramp that a hurry-up can use. */
const RAMP_NAMES: Partial<Record<ShotId, string>> = {
  rampLeft: "LEFT RAMP",
  rampRight: "RIGHT RAMP",
};

/** The name of the shot that collects the extra ball. */
const EXTRA_SHOT_NAME = "RIGHT ORBIT";

/** Whole seconds that are left. Partial seconds count as a full second. */
function secondsLeft(msLeft: number): number {
  return Math.ceil(msLeft / 1000);
}

/** The stage line of the HUD. */
export function stageLine(def: StageDef, total: number): string {
  return `STAGE ${def.number}/${total} ${def.label}`;
}

/** The twist line of the HUD. It is the one sentence that tells the twist. */
export function twistLine(def: StageDef): string {
  return def.twistText;
}

/**
 * The count that a goal shows now, from the stage signals.
 * A count goal never shows above its target.
 */
function goalProgress(goal: StageGoal, signals: Readonly<RunSignals>): number {
  const raw = rawProgress(goal, signals);
  return goal.kind === "score" ? raw : Math.min(raw, goal.target);
}

/** The count of a goal before the clamp to its target. */
function rawProgress(goal: StageGoal, signals: Readonly<RunSignals>): number {
  switch (goal.kind) {
    case "score":
      return signals.stagePoints;
    case "modes":
      return signals.modesFinished;
    case "multiball":
      return signals.multiballs;
    case "lit_shots":
      return signals.litShots;
    case "jackpots":
      return signals.jackpots;
  }
}

/** The goal text without the progress count. */
function goalText(goal: StageGoal): string {
  const t = goal.target;
  switch (goal.kind) {
    case "score":
      return `SCORE ${formatPoints(t)}`;
    case "modes":
      return `FINISH ${t} ${t === 1 ? "MODE" : "MODES"}`;
    case "multiball":
      return "START MULTIBALL";
    case "lit_shots":
      return `MAKE ${t} LIT SHOTS`;
    case "jackpots":
      return `${t} ${t === 1 ? "JACKPOT" : "JACKPOTS"}`;
  }
}

/**
 * The goal line of the HUD. The score goal shows its points, the other goals show a count.
 * While a boss fights, the goal is the boss: the stage goal is already met.
 */
export function goalLine(
  def: StageDef,
  signals: Readonly<RunSignals>,
  boss?: Pick<BossState, "active" | "name">,
): string {
  if (boss?.active) return `GOAL: BEAT THE BOSS (${boss.name.toUpperCase()})`;
  const { goal } = def;
  const p = goalProgress(goal, signals);
  const shown = goal.kind === "score" ? formatPoints(p) : String(p);
  const count = goal.kind === "score" ? `(${shown})` : `(${shown}/${goal.target})`;
  return `GOAL: ${goalText(goal)} ${count}`;
}

/** The HUD rows of the timed events, in row order. An empty row is "". */
export function eventLines(
  events: Readonly<Record<TimedEventKind, Readonly<TimedEventState>>>,
  extra: Readonly<ExtraBallState>,
  hurryShot: StageDef["hurryShot"],
): [string, string, string] {
  return [
    hurryRow(events.hurry_up, hurryShot),
    frenzyRow(events.frenzy),
    chainOrExtraRow(events.combo_chain, extra),
  ];
}

/** Row 1: the hurry-up while it runs. */
function hurryRow(hurry: Readonly<TimedEventState>, shot: StageDef["hurryShot"]): string {
  if (!hurry.active) return "";
  const ramp = RAMP_NAMES[shot] ?? "";
  return `HURRY-UP ${formatPoints(hurry.value)} · ${secondsLeft(hurry.msLeft)} s · ${ramp}`;
}

/** Row 2: the frenzy while it runs, or the bumper hits that build toward it. */
function frenzyRow(frenzy: Readonly<TimedEventState>): string {
  if (frenzy.active) return `FRENZY 2X SWITCHES · ${secondsLeft(frenzy.msLeft)} s`;
  return frenzy.count > 0 ? `BUMPERS ${frenzy.count}/${FRENZY_HITS}` : "";
}

/**
 * Row 3: the combo chain once a combo has hit. A window with no combo yet shows nothing.
 * The extra ball shows when no chain runs.
 */
function chainOrExtraRow(
  chain: Readonly<TimedEventState>,
  extra: Readonly<ExtraBallState>,
): string {
  if (chain.active && chain.count >= 1) {
    return `COMBO x${chain.count} · ${secondsLeft(chain.msLeft)} s`;
  }
  return extra.lit ? `EXTRA BALL LIT · ${EXTRA_SHOT_NAME}` : "";
}

/** The label, the text and the owned count of a perk card. `owned` is "" for 0 copies. */
export function perkCard(
  def: PerkDef,
  owned: number,
): { label: string; text: string; owned: string } {
  return {
    label: def.label,
    text: def.text,
    owned: owned > 0 ? `OWNED ${owned}/${def.maxStacks}` : "",
  };
}

/** The countdown of a featured draft. */
export function autoPickLine(msLeft: number): string {
  return `AUTO PICK IN ${secondsLeft(msLeft)} s: CARD 1`;
}

/** One line of the level select. A stage above `unlocked` shows how to open it. */
export function levelLine(def: StageDef, best: number, unlocked: number): string {
  const head = `STAGE ${def.number} ${def.label}`;
  if (def.number <= unlocked) return `${head} · BEST ${formatPoints(best)}`;
  return `${head} · CLEAR STAGE ${def.number - 1} TO OPEN`;
}

/** One line of the game over list for a stage that ran. */
export function overStageLine(def: StageDef, points: number, best: number, isNew: boolean): string {
  const tail = isNew ? "NEW BEST" : `BEST ${formatPoints(best)}`;
  return `STAGE ${def.number} ${def.label} ${formatPoints(points)} · ${tail}`;
}
